import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Observable, catchError, concatMap, map, of, tap, throwError } from 'rxjs';
import { ApiError, AuthError, JwtError } from '@newroom-connect/library/errors';
import { JwtHelper } from '@newroom-connect/library/helpers';
import {
  IConfirmRegistrationInput,
  IDecodedTokenData,
  IEmailInput,
  ILoginInput,
  IRegistrationInput,
  IResetPasswordInput,
  ISessionTokensResponse
} from '@newroom-connect/library/interfaces';

import { ApiService } from '../api/api.service';
import { LoggingService } from '../logging/logging.service';
import { SessionService } from '../session/session.service';
import { StateService } from '../state/state.service';

/**
 * HttpContext token to skip session refresh.
 * Useful for scenarios where we want to skip session refresh (Example: Skip auth related requests (login, registration, etc.) in the HttpAuthInterceptor).
 */
export const SKIP_SESSION_REFRESH = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ApiService {
  // HOTFIX: Workaround for codes that do not meet password or email requirements
  private readonly FALLBACK_CODES: Record<string, string> = {
    'PIP_MOCUK_VS2': 'Pip_MocUk_VS2',
    'PIP_MOCUK_VS3': 'Pip_MocUk_VS3',
    'PIP_MOCUSA_VS2': 'Pip_MocUsa_VS2',
    'PIP_MOCUSA_VS3': 'Pip_MocUsa_VS3',
    'PIP_GDBosnia&Herzegovina_VS2': 'PIP_GDBosniaAndHerzegovina_VS2',
    'PIP_GDBosnia&Herzegovina_VS3': 'PIP_GDBosniaAndHerzegovina_VS3'
  };

  /**
   * @constructor
   *
   * @param http
   * @param loggingService
   * @param sessionService
   * @param stateService
   */
  constructor(
    protected override readonly http: HttpClient,
    protected override readonly loggingService: LoggingService,
    private readonly sessionService: SessionService,
    private readonly stateService: StateService
  ) {
    super(http, loggingService);
  }

  /**
   * Decodes an ID token and extracts username and redirectAreaSlug.
   *
   * @param idToken
   *
   * @returns
   *
   * @throws
   */
  private decodeIdToken(idToken: string): IDecodedTokenData {
    let idTokenDecoded: Record<string, string>;

    try {
      idTokenDecoded = JwtHelper.decodeToken(idToken);
    } catch (error) {
      throw new JwtError(error as Error);
    }

    return {
      username: idTokenDecoded['cognito:username'],
      redirectAreaSlug: idTokenDecoded['redirect:redirectAreaSlug']
    };
  }

  /**
   * Login to the system with the given input.
   * On successful login, the received tokens and session ID will be stored in the local storage.
   *
   * @param input
   *
   * @returns Observable containing the requested tokens and session ID.
   */
  public login(input: ILoginInput): Observable<ISessionTokensResponse> {
    return this.post<ISessionTokensResponse>('authenticate/login', input, {
      withCredentials: true,
      context: new HttpContext().set(SKIP_SESSION_REFRESH, true)
    }).pipe(
      catchError((error: ApiError) => throwError(() => AuthError.fromApiError(error))),
      tap({
        next: (response) => {
          const { username, redirectAreaSlug } = this.decodeIdToken(response.tokenSet?.idToken ?? '');

          if (!username) {
            throw new Error('Unable to login: No username available');
          }

          this.sessionService.setUserSession(response.tokenSet, response.sessionId, username, input.rememberMe, redirectAreaSlug || null, false, input.isCodeLogin);
        }
      })
    );
  }

  /**
   * Authenticate to a public project using the project slug.
   * On successful authentication, the received tokens and session ID will be stored in the local storage.
   *
   * @param projectSlug
   * @param code
   *
   * @returns
   */
  public authenticateProjectPublicUser(projectSlug: string, code?: string): Observable<ISessionTokensResponse> {
    const payload: { projectSlug: string; code?: string } = { projectSlug };

    if (code) {
      payload.code = code;
    }

    return this.post<ISessionTokensResponse>('authenticate/public', payload, { withCredentials: true }).pipe(
      catchError((error: ApiError) => throwError(() => AuthError.fromApiError(error))),
      tap({
        next: (response) => {
          const { username, redirectAreaSlug } = this.decodeIdToken(response.tokenSet?.idToken ?? '');

          if (!username) {
            throw new Error('Unable to authenticate to public project: No username available');
          }

          this.sessionService.setUserSession(response.tokenSet, response.sessionId, username, true, redirectAreaSlug || null, false, true);
        }
      })
    );
  }

  /**
   * Logout from the system.
   * Sends the current session ID to clean up the server-side session.
   *
   * @returns Observable that completes when logout is successful.
   */
  public logout(): Observable<void> {
    const sessionId = this.sessionService.getUserSession()?.getSessionId();

    return this.post<void>('authenticate/logout', { sessionId }, { withCredentials: true }).pipe(
      catchError((error: ApiError) => throwError(() => AuthError.fromApiError(error))),
      tap({
        complete: () => this.sessionService.clearUserSession()
      })
    );
  }

  /**
   *
   * @param input
   *
   * @returns
   */
  public register(input: IRegistrationInput): Observable<any> {
    return this.post('register', input, {
      context: new HttpContext().set(SKIP_SESSION_REFRESH, true)
    });
  }

  /**
   *
   * @param input
   *
   * @returns
   */
  public confirmRegistration(input: IConfirmRegistrationInput): Observable<any> {
    return this.post('register/confirm/code', input, {
      context: new HttpContext().set(SKIP_SESSION_REFRESH, true)
    });
  }

  /**
   *
   * @param input
   *
   * @returns
   */
  public resendConfirmation(input: IEmailInput): Observable<any> {
    return this.post('register/confirm/resend', input, {
      context: new HttpContext().set(SKIP_SESSION_REFRESH, true)
    });
  }

  /**
   * Request a password reset for the given email.
   *
   * @param input The email address to send the password reset request to.
   *
   * @returns
   */
  public forgotPassword(input: IEmailInput): Observable<any> {
    return this.post<any>('authenticate/password/request', input, {
      context: new HttpContext().set(SKIP_SESSION_REFRESH, true)
    });
  }

  /**
   * Reset the password with the given verification code and new password.
   *
   * @param input
   *
   * @returns
   */
  public resetPassword(input: IResetPasswordInput): Observable<any> {
    return this.post<any>('authenticate/password/reset', input, {
      context: new HttpContext().set(SKIP_SESSION_REFRESH, true)
    });
  }

  /**
   * Refresh the current user session.
   * On successful refresh, the refreshed tokens and session ID will be stored in the local storage.
   *
   * @returns Observable containing the requested tokens and session ID.
   *
   * @throws `JwtError` if the current ID token is not able to be decoded.
   */
  public refreshSession(): Observable<ISessionTokensResponse | null> {
    const userSession = this.sessionService.getUserSession();

    if (!userSession) {
      throw new Error('Unable to refresh session: No user session available');
    }

    const idToken = userSession.getIdToken();
    const refreshToken = userSession.getRefreshToken();

    const { username, redirectAreaSlug } = this.decodeIdToken(idToken ?? '');

    if (!username || !refreshToken) {
      return of(null);
    }

    return this.post<ISessionTokensResponse>(
      'authenticate/session/refresh',
      {
        username,
        refreshToken
      },
      { withCredentials: true }
    ).pipe(
      catchError((error: ApiError) => throwError(() => AuthError.fromApiError(error))),
      tap({
        next: (response) => this.sessionService.setUserSession(response.tokenSet, response.sessionId, username, this.sessionService.getUserSession()?.getRememberMe(), redirectAreaSlug || null)
      })
    );
  }

  /**
   * Refresh the current user session if the session needs a refresh.
   *
   * @param requestUrl An optional URL of the request to determine, if session request is even needed by checking the URL.
   *
   * @returns Observable performing the session refresh.
   */
  public refreshSessionIfNeeded(requestUrl?: string): Observable<ISessionTokensResponse | null> {
    // Do not refresh the session if the request will do it by itself.
    const isSessionRefreshRequest = requestUrl?.endsWith('authenticate/session/refresh');

    if (isSessionRefreshRequest) {
      return of(null);
    }

    const userSession = this.sessionService.getUserSession();

    // If the user session needs a refresh, set an app-wide loading state, refresh the session and disable the loading state.
    if (userSession?.getIdToken() && userSession?.needsRefresh()) {
      return of(this.stateService.setLoadingState(true)).pipe(
        concatMap(() => this.refreshSession()),
        map(response => {
          this.stateService.setLoadingState(false);

          return response;
        })
      );
    }

    return of(null);
  }

  /**
   * Validates a preview token and returns session tokens if valid.
   *
   * @param refreshToken The preview refresh token to validate.
   * @param username The username to validate.
   *
   * @returns Observable containing the session tokens and ID if valid.
   */
  public validatePreviewToken(refreshToken: string, username: string): Observable<ISessionTokensResponse> {
    return this.post<ISessionTokensResponse>(
      'authenticate/session/refresh',
      {
        username,
        refreshToken
      },
      { withCredentials: true }
    );
  }

  /**
   * Create credentials for code login.
   *
   * @param projectSlug - The (current) project slug.
   * @param code - The code to process.
   *
   * @returns The processed code.
   */
  public getCodeLoginCredentials(projectSlug: string, code: string): ILoginInput {
    const sanitizedCode = this.FALLBACK_CODES[code] || code;

    return {
      email: `invite+${projectSlug}-${sanitizedCode}@newroom-connect.com`,
      password: sanitizedCode,
      rememberMe: true,
      projectSlug: projectSlug,
      isCodeLogin: true
    };
  }
}
